import { useState, useEffect } from "react";
import { Chip, IconButton, Stack, Container, Grid } from "@mui/material";

import TuneIcon from "@mui/icons-material/Tune";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";

import DataGridCustom from "../components/General/DatagridCustom";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import AttachmentTypeSourceManager from "../components/LegalFileAttachment/AttachmentTypeSourceManager";
import AttachmentTypeEditDialog from "../components/LegalFileAttachment/AttachmentTypeEditDialog";

import { getAttachmentTypes } from "../services/AttachmentTypeService";

export default function SettingsAttachmentTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAttachmentTypes();
      setTypes(res?.data?.data ?? []);
    } catch {
      setError("Error al cargar los tipos de escritos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOpenManager = (type) => { setSelectedType(type); setManagerOpen(true); };
  const handleCloseManager = () => { setManagerOpen(false); setSelectedType(null); load(); };
  const handleOpenEdit = (type) => { setSelectedType(type); setEditOpen(true); };
  const handleCloseEdit = (refreshed) => { setEditOpen(false); setSelectedType(null); if (refreshed) load(); };

  const boolChip = (value, label) =>
    value ? (
      <Chip key={label} label={label} size="small" color="primary" variant="outlined"
        sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
    ) : null;

  const columns = [
    {
      field: "display_name", headerName: "Nombre", flex: 1, minWidth: 180,
      valueGetter: (value, row) => row.display_name || row.name,
    },
    {
      field: "status", headerName: "Estado", width: 100,
      renderCell: ({ value }) => (
        <Chip label={value === "enabled" ? "Activo" : "Inactivo"} size="small"
          color={value === "enabled" ? "success" : "default"} variant="outlined"
          sx={{ fontSize: 11, height: 22, fontWeight: 700 }} />
      ),
    },
    {
      field: "flags", headerName: "Opciones", flex: 1, minWidth: 260, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
          {boolChip(row.allow_upload,                     "Subida manual")}
          {boolChip(row.allow_multiple,                   "Múltiple")}
          {boolChip(row.allow_generate,                   "Generación")}
          {boolChip(row.allow_online_upload,              "Online")}
          {boolChip(!row.requires_file,                   "Sin archivo")}
          {boolChip(row.uses_lawsuit_file,                "Usa título")}
          {boolChip(row.use_for_notification_certificate, "Cédulas")}
        </Stack>
      ),
    },
    {
      field: "source_configs", headerName: "Fuentes", width: 110,
      renderCell: ({ value }) => {
        const count = value?.length ?? 0;
        return (
          <Chip
            label={count === 0 ? "Sin config" : `${count} fuente${count !== 1 ? "s" : ""}`}
            size="small" color={count > 0 ? "primary" : "default"}
            variant={count > 0 ? "filled" : "outlined"}
            sx={{ fontSize: 11, height: 22, fontWeight: 700 }} />
        );
      },
    },
    {
      field: "actions", headerName: "Acciones", width: 110, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)} title="Editar">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="secondary" onClick={() => handleOpenManager(row)} title="Fuentes">
            <TuneIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<FolderSpecialRoundedIcon />}
            title="Tipo de Escritos"
            description="Habilitá, deshabilitá y configurá el texto y la plantilla de cada tipo de escrito."
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <DataGridCustom rows={types} columns={columns} loading={loading} error={error} />
        </Grid>
      </Grid>

      <AttachmentTypeEditDialog open={editOpen} onClose={handleCloseEdit} attachmentType={selectedType} />
      <AttachmentTypeSourceManager open={managerOpen} onClose={handleCloseManager} attachmentType={selectedType} />
    </Container>
  );
}
